// Claude: Floating AI Chat drawer — wired to POST /ai-chat/message (Gemini) 25-02-2026
// Claude: unified with AI Chat page history via Redux aiChat slice; added parseOptions + buttons 23-03-2026
import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  IconButton,
  VStack,
  Spinner,
  Button,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { MdClose, MdSend, MdAutoAwesome, MdDeleteOutline } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../redux/Actions";
import { CommonAPI } from "../../Apis/commonApi";
import { SellerAPI } from "../../Apis/sellerAPI";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";

type Props = {
  role: "Buyer" | "Seller";
};

type ChatMessage = {
  id: number;
  text: string;
  sender: "ai" | "user";
  timestamp: Date;
};

// Claude: Parse <button>text</button> tags out of AI message text 23-03-2026
const parseOptions = (text: string): { prose: string; options: string[] } => {
  const buttonRegex = /<button>(.*?)<\/button>/gi;
  const options: string[] = [];
  let match;
  while ((match = buttonRegex.exec(text)) !== null) {
    options.push(match[1].trim());
  }
  const prose = text.replace(/<button>.*?<\/button>/gi, "").trim();
  return { prose, options };
};

const AiChatDrawer = ({ role }: Props) => {
  const dispatch = useDispatch();
  const menuState = useSelector((state: any) =>
    role === "Buyer" ? state.buyerMenu : state.sellerMenu
  );
  const messages: ChatMessage[] = useSelector((state: any) =>
    role === "Buyer" ? state.aiChat.buyerMessages : state.aiChat.sellerMessages
  );
  const isOpen = menuState?.aiChatOpen;
  const bottomRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const { error: toastError } = useToastWrapper();
  // Claude: feedback flow — awaitingFeedback intercepts next message; feedbackCount capped at 3/session 27-03-2026
  const [profile, setProfile] = useState<any>(null);
  const [awaitingFeedback, setAwaitingFeedback] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState(0);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Claude: fetch welcome from KB if history is empty when drawer first opens 23-03-2026
  useEffect(() => {
    if (isOpen) {
      if (messages.length === 0) fetchWelcome();
      // Claude: fetch profile for feedback submission if not yet loaded 27-03-2026
      if (!profile) {
        if (role === "Seller") {
          SellerAPI.getSellerProfile().then((res: any) => setProfile(res?.data?.data)).catch(() => {});
        } else {
          BuyerAPI.getBuyerProfile().then((res: any) => setProfile(res?.data?.data)).catch(() => {});
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [isOpen]);

  const addMessage = (msg: ChatMessage) => {
    if (role === "Buyer") dispatch(actions.aiChat.addBuyerMessage(msg));
    else dispatch(actions.aiChat.addSellerMessage(msg));
  };

  const fetchWelcome = async () => {
    try {
      const res = await CommonAPI.getAiWelcome(role);
      const text = typeof res === "string" ? res : res?.data;
      if (text) {
        const welcomeMsg: ChatMessage = { id: 1, text, sender: "ai", timestamp: new Date() };
        if (role === "Buyer") dispatch(actions.aiChat.setBuyerMessages([welcomeMsg]));
        else dispatch(actions.aiChat.setSellerMessages([welcomeMsg]));
      }
    } catch {}
  };

  const handleShowOptions = async () => {
    try {
      const res = await CommonAPI.getAiWelcome(role);
      const text = typeof res === "string" ? res : res?.data;
      if (text) {
        addMessage({ id: Date.now(), text, sender: "ai", timestamp: new Date() });
      }
    } catch {}
  };

  const handleSend = async (overrideText?: string) => {
    const text = (overrideText ?? inputText).trim();
    if (!text || loading) return;
    if (!overrideText) setInputText("");
    const userMsg: ChatMessage = { id: Date.now(), text, sender: "user", timestamp: new Date() };
    addMessage(userMsg);

    // Claude: intercept — submit feedback if awaiting, skip AI call 27-03-2026
    if (awaitingFeedback) {
      setAwaitingFeedback(false);
      try {
        const personName = role === "Seller" ? (profile?.personName || "") : (profile?.buyerPersonName || "");
        const companyName = role === "Seller" ? (profile?.companyName || "") : (profile?.buyerCompanyName || "");
        await CommonAPI.submitFeedback({ role, personName, companyName, feedbackText: text });
        setFeedbackCount((c) => c + 1);
      } catch {}
      addMessage({ id: Date.now() + 1, text: "Thank you for the feedback. We will process this for further action as appropriate.", sender: "ai", timestamp: new Date() });
      return;
    }

    setLoading(true);
    try {
      const history = messages.map((m) => ({ role: m.sender === "ai" ? "model" : "user", text: m.text }));
      const res = await CommonAPI.sendAiMessage({ message: text, conversationHistory: history, eventId: menuState?.eventId });
      const reply = res?.data?.reply ?? "Sorry, I could not respond.";
      addMessage({ id: Date.now() + 1, text: reply, sender: "ai", timestamp: new Date() });
    } catch {
      addMessage({ id: Date.now() + 1, text: "Something went wrong. Please try again.", sender: "ai", timestamp: new Date() });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm("Clear entire chat history?")) return;
    if (role === "Buyer") dispatch(actions.aiChat.clearBuyerMessages());
    else dispatch(actions.aiChat.clearSellerMessages());
    fetchWelcome();
  };

  // Claude: intercept "Feedback" option locally; forward others to handleSend 27-03-2026
  const handleOption = (opt: string) => {
    const clean = opt.replace(/^\d+\.\s+/, "").trim();
    if (clean.toLowerCase() === "feedback") {
      if (feedbackCount >= 3) {
        toastError("You have already submitted 3 feedbacks this session.");
        return;
      }
      setAwaitingFeedback(true);
      addMessage({ id: Date.now(), text: "Please share your feedback.", sender: "ai", timestamp: new Date() });
      return;
    }
    handleSend(clean);
  };

  const close = () => {
    const action = role === "Buyer"
      ? actions.buyerMenu.set({ activeMenu: menuState.activeMenu, aiChatOpen: false })
      : actions.sellerMenu.set({ activeMenu: menuState.activeMenu, aiChatOpen: false });
    dispatch(action);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — covers full screen incl. jumbotron; click anywhere to close 30-03-2026 */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom="65px"
        bg="blackAlpha.400"
        zIndex={100}
        onClick={close}
        style={{ maxWidth: "600px", margin: "0 auto" }}
      />

      {/* Drawer panel — sits above nav bar, top edge just below jumbotron 30-03-2026 */}
      <Box
        position="fixed"
        top="110px"
        bottom="65px"
        width="100%"
        maxWidth="596px"
        bg="white"
        borderTop="2px solid #0E122F"
        zIndex={101}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        {/* Drawer header — Claude: green bg matching seller catalog tag container 30-03-2026 */}
        <Flex
          px={5}
          py={3}
          bg="#E8F5E9"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap={2}>
            <MdAutoAwesome size={18} color="#2E7D32" />
            <Text fontSize="sm" fontWeight={600} color="#1C1B1F">AFTR AI</Text>
          </Flex>
          <MdClose size={20} color="#1C1B1F" cursor="pointer" onClick={close} />
        </Flex>

        {/* Messages area */}
        <VStack
          flex={1}
          overflowY="auto"
          px={4}
          py={4}
          spacing={3}
          alignItems="stretch"
        >
          {messages.map((msg, i) =>
            msg.sender === "user" ? (
              <Flex key={msg.id} justifyContent="flex-end">
                <Box bg="#0E122F" borderRadius="12px 0 12px 12px" px={3} py={2} maxW="80%">
                  <Text fontSize="13px" lineHeight="1.6" color="white">{msg.text}</Text>
                </Box>
              </Flex>
            ) : (
              <Flex key={msg.id} alignItems="flex-start" gap={2}>
                <Box minW="28px" h="28px" borderRadius="full" bg="#0E122F" display="flex" alignItems="center" justifyContent="center" flexShrink={0} mt="2px">
                  <MdAutoAwesome size={14} color="#FF5F12" />
                </Box>
                <Box bg="#F3F0FF" borderRadius="0 12px 12px 12px" px={3} py={2} maxW="85%">
                  {(() => {
                    const { prose, options } = parseOptions(msg.text);
                    return (
                      <>
                        {prose && (
                          <Text fontSize="13px" lineHeight="1.6" color="#1C1B1F" whiteSpace="pre-wrap">{prose}</Text>
                        )}
                        {options.length > 0 && (
                          <Wrap mt={prose ? 2 : 0} spacing={2}>
                            {options.map((opt) => (
                              <WrapItem key={opt}>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  borderColor="#7C66FF"
                                  color="#7C66FF"
                                  borderRadius="full"
                                  fontSize="12px"
                                  fontWeight={500}
                                  px={3}
                                  h="28px"
                                  isDisabled={loading}
                                  _hover={{ bg: "#7C66FF", color: "white" }}
                                  onClick={() => handleOption(opt)}
                                >
                                  {opt}
                                </Button>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                        {i !== 0 && (
                          <Text fontSize="10px" color="gray.400" mt={2} cursor="pointer" _hover={{ color: "#7C66FF" }} onClick={handleShowOptions}>
                            Click here for Main Menu
                          </Text>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Flex>
            )
          )}

          {/* Loading indicator */}
          {loading && (
            <Flex alignItems="center" gap={2}>
              <Box minW="28px" h="28px" borderRadius="full" bg="#0E122F" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <MdAutoAwesome size={14} color="#FF5F12" />
              </Box>
              <Spinner size="sm" color="#7C66FF" />
            </Flex>
          )}
          <div ref={bottomRef} />
        </VStack>

        {/* Input bar */}
        <Flex px={4} py={3} borderTop="1px solid #eee" alignItems="center" gap={2} bg="white">
          <IconButton
            aria-label="Clear chat"
            icon={<MdDeleteOutline size={18} />}
            variant="ghost"
            borderRadius="full"
            size="sm"
            color="gray.400"
            _hover={{ color: "red.400", bg: "red.50" }}
            isDisabled={loading || messages.length === 0}
            onClick={handleClear}
            title="Clear chat"
          />
          <Input
            placeholder="Ask me anything..."
            fontSize="13px"
            borderRadius="20px"
            bg="#F7F7F7"
            border="none"
            _focus={{ border: "1px solid #7C66FF", bg: "white" }}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            isDisabled={loading}
          />
          <IconButton
            aria-label="Send"
            icon={<MdSend size={18} />}
            borderRadius="full"
            bg="#0E122F"
            color="white"
            size="sm"
            isDisabled={loading || !inputText.trim()}
            onClick={() => handleSend()}
            _hover={{ bg: "#1a1f45" }}
          />
        </Flex>
      </Box>
    </>
  );
};

export default AiChatDrawer;
